/**
 * Settings > Your agents, for tenant admins: the firm's default for each policy, which applies to everyone who has not
 * set their own, and a lock that makes it apply to everyone, whatever they set. Admins also switch off suggestions
 * learned from how the firm's people work.
 */
import { Alert, Box, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';
import { POLICY_KEYS, POLICY_PRESETS, type FirmPolicies as FirmPoliciesData, type FirmPolicy, type PolicyKey, type PolicyValues } from '@diggens/types';
import { PolicyCard } from './PolicyFields';
import { MY_POLICIES_QUERY_KEY, MY_PROPOSAL_QUERY_KEY } from './AgentPreferences';

const FIRM_POLICIES_QUERY_KEY = ['policies', 'firm'] as const;
const LEARNING_QUERY_KEY = ['policies', 'learning'] as const;

/** Whether people get suggestions learned from how they work; off withdraws the ones still waiting. */
function LearningSwitch() {
  const queryClient = useQueryClient();
  const learning = useQuery({ queryKey: LEARNING_QUERY_KEY, queryFn: () => apiFetch<{ enabled: boolean }>('/policies/learning'), refetchOnMount: 'always' });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => apiFetch<void>('/policies/learning', { method: 'PUT', body: JSON.stringify({ enabled }) }),
    // Switching it off withdraws the admin's own suggestion too
    onSettled: () => Promise.all([queryClient.invalidateQueries({ queryKey: LEARNING_QUERY_KEY }), queryClient.invalidateQueries({ queryKey: MY_PROPOSAL_QUERY_KEY })]),
  });

  if (learning.isError) return <Alert severity="error">Could not load whether suggestions are on.</Alert>;
  return (
    <Box sx={{ maxWidth: 800 }}>
      <FormControlLabel
        label="Suggest preferences learned from how people work"
        disabled={learning.isPending || toggle.isPending}
        control={<Switch checked={learning.data?.enabled ?? false} onChange={(event) => toggle.mutate(event.target.checked)} />}
      />
      <Typography variant="body2" color="text.secondary">
        Diggens may suggest a preference to each person from the edits they make before approving, the corrections they type and when they work. Each suggestion waits for that person to use it; nothing learned applies on its own. Turning
        this off withdraws suggestions still waiting.
      </Typography>
      {toggle.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not save this. Try again.
        </Alert>
      )}
    </Box>
  );
}

export function FirmPolicies() {
  const queryClient = useQueryClient();
  // Read again on every visit and on coming back to the tab, since another admin may have changed a default
  const policies = useQuery({ queryKey: FIRM_POLICIES_QUERY_KEY, queryFn: () => apiFetch<FirmPoliciesData>('/policies/firm'), refetchOnMount: 'always', refetchOnWindowFocus: true });
  // A firm default changes what applies to the admin too
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: FIRM_POLICIES_QUERY_KEY }), queryClient.invalidateQueries({ queryKey: MY_POLICIES_QUERY_KEY })]);

  // A card starts over after its own save or removal succeeds, from what the server holds then
  const [saves, setSaves] = useState<Partial<Record<PolicyKey, number>>>({});
  const startOver = (key: PolicyKey) => setSaves((counts) => ({ ...counts, [key]: (counts[key] ?? 0) + 1 }));

  const save = useMutation({
    mutationFn: ({ key, value, locked }: { key: PolicyKey; value: PolicyValues[PolicyKey]; locked: boolean }) =>
      apiFetch<void>(`/policies/firm/${key}`, { method: 'PUT', body: JSON.stringify({ value, locked }) }),
    onSuccess: (_saved, { key }) => startOver(key),
    onSettled: refresh,
  });
  const reset = useMutation({
    mutationFn: (key: PolicyKey) => apiFetch<void>(`/policies/firm/${key}`, { method: 'DELETE' }),
    onSuccess: (_removed, key) => startOver(key),
    onSettled: refresh,
  });

  if (policies.isPending) {
    return (
      <Box>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (policies.isError) return <Alert severity="error">Could not load the firm defaults.</Alert>;

  const busy = (key: PolicyKey) => (save.isPending && save.variables?.key === key) || (reset.isPending && reset.variables === key);

  return (
    <Stack spacing={3}>
      <Box sx={{ maxWidth: 800 }}>
        <Typography variant="h5">Firm defaults</Typography>
        <Typography color="text.secondary">
          These apply to everyone at the firm who has not set their own. Lock one to make it apply to everyone. Like personal preferences, they never let an agent skip an approval.
        </Typography>
      </Box>
      <LearningSwitch />
      {POLICY_KEYS.map((key) => {
        const policy = policies.data[key] as FirmPolicy<typeof key>;
        const value = policy.value;
        return (
          <PolicyCard
            key={`${key}:${saves[key] ?? 0}`}
            policyKey={key}
            initial={value ?? POLICY_PRESETS[key]}
            revision={policy}
            source={value === null ? 'No firm default' : policy.locked ? 'Locked for everyone' : 'Firm default'}
            disabled={false}
            saving={busy(key)}
            error={(save.isError && save.variables?.key === key) || (reset.isError && reset.variables === key) ? 'Could not save this. Try again.' : null}
            onSave={(next) => save.mutate({ key, value: next, locked: policy.locked })}
            onReset={value !== null ? () => reset.mutate(key) : undefined}
            resetLabel="Remove firm default"
            saveUnchanged={value === null}
            // Flipping the lock saves the stored default and starts the card over, which would drop unsaved edits, so it
            // waits until they are saved
            actions={(edited) => (
              <FormControlLabel
                label="Lock for everyone"
                disabled={value === null || busy(key) || edited}
                control={<Switch checked={policy.locked} onChange={(event) => value !== null && save.mutate({ key, value, locked: event.target.checked })} />}
              />
            )}
          />
        );
      })}
    </Stack>
  );
}
